import { RefObject, cloneElement, useCallback, useEffect, useMemo, useState } from "react";
import { isTruck, SelectedMarker } from ".";
import permissionNames from "../../common/auth/access-control/permissionNames";
import { useActionPermissions } from "../../common/auth/useActionPermissions";
import { ArrowsOrientation, DatePager } from "../common/mui-inputs/DatePager";
import { GoogleMapControls } from "../locations/types";
import { DashboardVehicleControls } from "./DashboardVehicleControls";
import { DashboardVehicleHosGraph } from "./DashboardVehicleHosGraph";
import { Divider, Tooltip } from "@material-ui/core";
import { LocationOn } from "@material-ui/icons";
import { Button } from "react-admin";
import { convertDecimalCoordinateToDegrees } from "../../common/util/convertDecimalCoordinateToDegrees";
import { addDays } from "date-fns";
import { LocationHistoryEntryDto } from "../../common/data/service-proxies";

interface Props {
  selectedVehicle: SelectedMarker;
  onSetHighlightedRoute: (
    highlightedRoute?: {
      lat: number;
      lng: number;
    }[]
  ) => void;
  onSetVisitedRoute: (
    highlightedRoute?: {
      lat: number;
      lng: number;
    }[]
  ) => void;
  onShareLinksClick: () => void;
  onSetIsPlaying: (isPlaying: boolean) => void;
  onCurrentLocationChange: (currentLocation: LocationHistoryEntryDto) => void;
  mapRef: RefObject<GoogleMapControls>;
}

export const DashboardControls = ({
  selectedVehicle,
  onSetHighlightedRoute,
  onSetVisitedRoute,
  onShareLinksClick,
  onSetIsPlaying,
  onCurrentLocationChange,
  mapRef,
}: Props) => {
  let currentDate = new Date();
  const [date, setDate] = useState<Date>(currentDate);
  const [position, setPosition] = useState<number>();
  const [currentLocation, setCurrentLocation] = useState<
    LocationHistoryEntryDto
  >();
  const actionPermissions = useActionPermissions();
  const name = useMemo(
    () =>
      isTruck(selectedVehicle)
        ? undefined
        : selectedVehicle.truckOrTrailer.name,
    [selectedVehicle]
  );

  const handlePlayingChange = useCallback(
    (playingValue: boolean) => {
      onSetIsPlaying(playingValue);
    },
    [onSetIsPlaying]
  );


  useEffect(() => {
    if(currentLocation) {
      onCurrentLocationChange(currentLocation)
    }
  }, [currentLocation, onCurrentLocationChange])

  return (
    <div className="vehicle-controls-component-wrapper">
      <DatePager
        selectedDateVariant="body1"
        selectedDateFormat="MMM do"
        selectedDateColor="primary"
        arrowsOrientation={ArrowsOrientation.Vertical}
        value={date}
        nextEnabled={addDays(date, 1) <= new Date()}
        onSetCurrentDate={(value) => {
          if (!!value) {
            setDate(value);
          }
        }}
        previousOnClick={() => {
          setDate(addDays(date, -1));
        }}
        nextOnClick={() => {
          const nextDate = addDays(date, 1);
          if (nextDate > new Date()) {
            return;
          }

          setDate(nextDate);
        }}
      />
      <div className="vehicle-controls-component">
        <DashboardVehicleControls
          vehicleId={selectedVehicle.truckOrTrailer.id}
          name={name}
          isTruck={selectedVehicle.isTruck}
          date={date}
          onSetHighlightedRoute={onSetHighlightedRoute}
          onSetVisitedRoute={onSetVisitedRoute}
          onSetPosition={setPosition}
          onShareLinksClick={onShareLinksClick}
          onPlayingChange={handlePlayingChange}
          onSetCurrentLocation={setCurrentLocation}
          mapRef={mapRef}
          driverId={
            isTruck(selectedVehicle)
              ? selectedVehicle.truckOrTrailer.driverId
              : undefined
          }
        />
        <Divider orientation="horizontal" />
        <div className="lower-controls">
          {isTruck(selectedVehicle) &&
          actionPermissions &&
          actionPermissions[permissionNames.hos.logs.view] ? (
            <div className="hos-graphs-wrapper">
              <DashboardVehicleHosGraph
                position={position}
                date={date}
                vehicleId={selectedVehicle.truckOrTrailer.id}
              />
            </div>
          ) : null}
          <div className="keep-right">
            <div className="location-display">
              <Tooltip title="View in Google Maps">
                {cloneElement(
                  <Button
                    component="a"
                    href={`https://www.google.com/maps/place/${convertDecimalCoordinateToDegrees(
                      currentLocation?.lat
                        ? currentLocation.lat
                        : selectedVehicle.truckOrTrailer.lat!,
                      "lat"
                    )}+${convertDecimalCoordinateToDegrees(
                      currentLocation?.lng
                        ? currentLocation.lng
                        : selectedVehicle.truckOrTrailer.lng!,
                      "lng"
                    )}/@${
                      currentLocation?.lat
                        ? currentLocation.lat
                        : selectedVehicle.truckOrTrailer.lat
                    },${
                      currentLocation?.lng
                        ? currentLocation.lng
                        : selectedVehicle.truckOrTrailer.lng
                    }`}
                    variant="outlined"
                    label={
                      currentLocation?.geolocation
                        ? currentLocation.geolocation
                        : isTruck(selectedVehicle)
                        ? selectedVehicle.truckOrTrailer.location ?? ""
                        : selectedVehicle.truckOrTrailer.geolocation ?? ""
                    }>
                    <LocationOn />
                  </Button>,
                  { target: "_blank" }
                )}
              </Tooltip>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
